import { SpinBoundaryEndActionHandler, type ReplayPlugin } from "@slot/client-engine";
import type { PresentationTaskRunner } from "@slot/presentation";
import type { AutoSpinStore } from "../autospin/AutoSpinStore";
import type { FeatureStateStore } from "../feature-state/FeatureStateStore";

/**
 * A hold at the spin end before the NEXT AUTO-ADVANCED spin (free spins and the base game's autospin) cuts in; a
 * manual base spin idles here and needs none. A longer linger on a win, a short breath on a round with none, so an
 * auto-advancing dead spin does not snap straight into the next reveal. The same perceived hold in every speed mode:
 * on the engine clock (it pauses and frame-steps) but multiplied by the time scale to cancel `sleep()`'s own scaling.
 * Listed after the other spin-end listeners (decision 46), but for the idle win re-show's start, which follows the
 * hold (decision 65).
 */
export function createAutoAdvanceHoldPlugin(deps: {
  featureStateStore: FeatureStateStore;
  autoSpinStore: AutoSpinStore;
  runner: PresentationTaskRunner;
  postWinHoldSeconds: number;
  postRoundHoldSeconds: number;
}): ReplayPlugin {
  return {
    name: "auto-advance-hold",
    actionHandlers: [
      new SpinBoundaryEndActionHandler(async (input) => {
        const won = BigInt(input.spinWinAmount ?? "0") > 0n;
        const autoAdvancing = deps.featureStateStore.isFeatureActive() || deps.autoSpinStore.getSnapshot().running;
        if (!autoAdvancing) return;
        const holdSeconds = won ? deps.postWinHoldSeconds : deps.postRoundHoldSeconds;
        if (holdSeconds > 0) await deps.runner.sleep(holdSeconds * deps.runner.getTimeScale());
      }),
    ],
  };
}
